import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { Toaster } from './components/ui/sonner';
import ErrorBoundary from './components/ErrorBoundary';
import './App.css';

// Eager-loaded entry point only (marketing landing — must load instant for SEO)
import MarketingWebsite from './pages/MarketingWebsite';

// Lazy-loaded public pages (separate chunks — keep marketing bundle minimal)
const AuthPage = lazy(() => import('./pages/AuthPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));

// Lazy-loaded dashboard pages (split into separate chunks to keep initial bundle small)
const OwnerDashboard = lazy(() => import('./pages/owner/OwnerDashboard'));
const PropertiesPage = lazy(() => import('./pages/owner/PropertiesPage'));
const ExpensesPage = lazy(() => import('./pages/owner/ExpensesPage'));
const ManagersPage = lazy(() => import('./pages/owner/ManagersPage'));
const ReportHistoryPage = lazy(() => import('./pages/owner/ReportHistoryPage'));
const InventoryPage = lazy(() => import('./pages/owner/InventoryPage'));
const HousekeepingPage = lazy(() => import('./pages/owner/HousekeepingPage'));
const HousekeepingTeamPage = lazy(() => import('./pages/owner/HousekeepingTeamPage'));
const HousekeeperMagicPage = lazy(() => import('./pages/HousekeeperMagicPage'));
const HousekeeperDashboardPage = lazy(() => import('./pages/housekeeper/HousekeeperDashboardPage'));
const CalendarSyncPage = lazy(() => import('./pages/owner/CalendarSyncPage'));
const CFODashboardPage = lazy(() => import('./pages/owner/CFODashboardPage'));
const SubscriptionPage = lazy(() => import('./pages/owner/SubscriptionPage'));
const ConciergeIncomePage = lazy(() => import('./pages/owner/ConciergeIncomePage'));
const VendorContractsPage = lazy(() => import('./pages/owner/VendorContractsPage'));
const TaxEstimatorPage = lazy(() => import('./pages/owner/TaxEstimatorPage'));
const PropertyValuationPage = lazy(() => import('./pages/owner/PropertyValuationPage'));
const PortfolioDashboard = lazy(() => import('./pages/owner/PortfolioDashboard'));
const PortfolioCinema = lazy(() => import('./pages/owner/PortfolioCinema'));
const AlertsCenter = lazy(() => import('./pages/owner/AlertsCenter'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const ManagerDashboard = lazy(() => import('./pages/manager/ManagerDashboard'));
const VendorDashboard = lazy(() => import('./pages/vendor/VendorDashboard'));

// Legal Pages
const PrivacyPolicy = lazy(() => import('./pages/legal/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/legal/TermsOfService'));
const SecurityPage = lazy(() => import('./pages/legal/SecurityPage'));

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-sand-warm">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  // Check both React state and localStorage (handles race condition after registration)
  const storedUser = !user ? (() => {
    try {
      const stored = localStorage.getItem('villapilot_user');
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  })() : null;
  
  const activeUser = user || storedUser;

  if (!activeUser) {
    return <Navigate to="/auth" />;
  }

  if (requiredRole && activeUser.role !== requiredRole) {
    if (activeUser.role === 'owner') return <Navigate to="/dashboard/owner" />;
    if (activeUser.role === 'manager') return <Navigate to="/dashboard/manager" />;
    if (activeUser.role === 'vendor') return <Navigate to="/dashboard/vendor" />;
  }

  return children;
};

function App() {
  return (
  <ErrorBoundary>
  <LanguageProvider>
    <AuthProvider>
      <BrowserRouter>
        <div className="App">
          <Suspense fallback={
            <div className="min-h-screen flex items-center justify-center bg-sand-warm">
              <div className="w-10 h-10 border-2 border-gold-metallic border-t-transparent rounded-full animate-spin" aria-label="Loading"></div>
            </div>
          }>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<MarketingWebsite />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/housekeeping/access/:token" element={<HousekeeperMagicPage />} />
            <Route
              path="/dashboard/housekeeper"
              element={
                <ProtectedRoute>
                  <HousekeeperDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            
            {/* Legal Pages - Public */}
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/security" element={<SecurityPage />} />

            {/* Owner Routes */}
            <Route
              path="/dashboard/owner"
              element={
                <ProtectedRoute requiredRole="owner">
                  <OwnerDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/owner/properties"
              element={
                <ProtectedRoute requiredRole="owner">
                  <PropertiesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/owner/expenses"
              element={
                <ProtectedRoute requiredRole="owner">
                  <ExpensesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/owner/managers"
              element={
                <ProtectedRoute requiredRole="owner">
                  <ManagersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/owner/reports"
              element={
                <ProtectedRoute requiredRole="owner">
                  <ReportHistoryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/owner/inventory"
              element={
                <ProtectedRoute requiredRole="owner">
                  <InventoryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/owner/housekeeping"
              element={
                <ProtectedRoute requiredRole="owner">
                  <HousekeepingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/owner/housekeeping-team"
              element={
                <ProtectedRoute requiredRole="owner">
                  <HousekeepingTeamPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/owner/cfo"
              element={
                <ProtectedRoute requiredRole="owner">
                  <CFODashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/owner/calendar-sync"
              element={
                <ProtectedRoute requiredRole="owner">
                  <CalendarSyncPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/owner/subscription"
              element={
                <ProtectedRoute requiredRole="owner">
                  <SubscriptionPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/owner/concierge"
              element={
                <ProtectedRoute requiredRole="owner">
                  <ConciergeIncomePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/owner/vendor-contracts"
              element={
                <ProtectedRoute requiredRole="owner">
                  <VendorContractsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/owner/taxes"
              element={
                <ProtectedRoute requiredRole="owner">
                  <TaxEstimatorPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/owner/valuation"
              element={
                <ProtectedRoute requiredRole="owner">
                  <PropertyValuationPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/owner/admin"
              element={
                <ProtectedRoute requiredRole="owner">
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/owner/portfolio/cinema"
              element={
                <ProtectedRoute requiredRole="owner">
                  <PortfolioCinema />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/owner/portfolio"
              element={
                <ProtectedRoute requiredRole="owner">
                  <PortfolioDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/owner/alerts"
              element={
                <ProtectedRoute requiredRole="owner">
                  <AlertsCenter />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/manager/alerts"
              element={
                <ProtectedRoute requiredRole="manager">
                  <AlertsCenter />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/owner/*"
              element={
                <ProtectedRoute requiredRole="owner">
                  <OwnerDashboard />
                </ProtectedRoute>
              }
            />

            {/* Manager Routes */}
            <Route
              path="/dashboard/manager"
              element={
                <ProtectedRoute requiredRole="manager">
                  <ManagerDashboard section="overview" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/manager/expenses"
              element={
                <ProtectedRoute requiredRole="manager">
                  <ManagerDashboard section="expenses" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/manager/maintenance"
              element={
                <ProtectedRoute requiredRole="manager">
                  <ManagerDashboard section="maintenance" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/manager/inventory"
              element={
                <ProtectedRoute requiredRole="manager">
                  <ManagerDashboard section="inventory" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/manager/*"
              element={
                <ProtectedRoute requiredRole="manager">
                  <ManagerDashboard section="overview" />
                </ProtectedRoute>
              }
            />

            {/* Vendor Routes */}
            <Route
              path="/dashboard/vendor"
              element={
                <ProtectedRoute requiredRole="vendor">
                  <VendorDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/vendor/*"
              element={
                <ProtectedRoute requiredRole="vendor">
                  <VendorDashboard />
                </ProtectedRoute>
              }
            />

            {/* Catch all */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          </Suspense>
          <Toaster />
        </div>
      </BrowserRouter>
    </AuthProvider>
  </LanguageProvider>
  </ErrorBoundary>
  );
}

export default App;
